import { queryString } from '@shared/hooks/recoil-query';
import { useCachedResource } from '@shared/hooks/useCachedResource';
import { api_ } from '@shared/plugIn/axios';
import { kstDayEndISO, kstDayStartISO } from '@shared/utils/kstTime.util';

import { BugoStatFevent } from './adminBugoStat.types';
import { BrandNameResolver, useAnalyticsBrandNames } from './useAnalyticsBrandNames';

/**
 * 한 번에 끌어올 상가 수 상한.
 * 전용 집계 API가 없어 프론트에서 집계하므로(스펙: docs/admin-bugo-stat-spec.md),
 * 응답이 지나치게 커지지 않도록 자른다. 잘린 경우 화면에 경고를 띄운다.
 */
export const FEVENT_FETCH_LIMIT = 20000;

/**
 * 조회 가능한 최대 기간.
 * 반기(약 184일)까지 한 번에 보려고 열어 뒀다 — 실제 안전장치는 이 일수가 아니라
 * FEVENT_FETCH_LIMIT(문서 수)이고, 넘치면 화면에 "일부만 집계" 경고가 뜬다.
 */
export const MAX_RANGE_DAYS = 200;

//* 집계 + 자세히 보기 목록에 필요한 필드만.
//* populate가 걸린 bugoBrand·funeralHomeInfo는 select에 반드시 포함해야 한다.
const FEVENT_SELECT = [
  'bugoBrand',
  'bugoBrandAgencyIndex',
  'useAcqMsg',
  'registerNumber',
  'meta.shareCount',
  'meta.isDeleted',
  'deceasedInfo.name',
  'deceasedInfo.coffinOut.date',
  'funeralHomeInfo',
  'funeralHomeInfoEmbed.name',
  'roomInfoEmbed.name',
  'createdAt',
].join(' ');

//* 상조명은 이름 맵(`useAnalyticsBrandNames`)으로 붙인다 — populate하면 bugoAgencyList가
//* 문서마다 통째로 복사돼 응답이 부푼다. 맵을 못 받았을 때만 폴백으로 붙인다.
const BRAND_POPULATE = {
  path: 'bugoBrand',
  select:
    'type bugoAgency.corpName bugoAgency.nickName bugoAgencyList.corpName bugoAgencyList.nickName',
};

const FUNERAL_HOME_POPULATE = { path: 'funeralHomeInfo', select: 'name' };

const BUGO_POPULATE = { path: 'bugos', select: 'lastSentPhoneNumber' };

//* 조의글은 개수만 세므로 isBan만 받는다 (_id는 항상 따라온다) — 본문까지 받으면 응답이 폭발한다
const ACQ_MSG_POPULATE = { path: 'acqMsgs', select: 'isBan' };

/**
 * 참여 수치(부고 발송·조의글)를 같이 받을지.
 *
 * ⚠️ `bugos`·`acqMsgs`는 **상한 없는 virtual populate**다 — 상가 2만 건이면 거기 딸린
 * 부고·조의글이 통째로 딸려온다. 응답 크기도, 파싱(날짜 리바이버가 전부 훑는다)도
 * 여기서 폭발한다. 그래서 기본은 끄고 사용자가 켤 때만 받는다.
 */
const populateOf = (engagement: boolean, brandPopulate: boolean) => {
  const base = brandPopulate
    ? [BRAND_POPULATE, FUNERAL_HOME_POPULATE]
    : [FUNERAL_HOME_POPULATE];
  return engagement ? [...base, BUGO_POPULATE, ACQ_MSG_POPULATE] : base;
};

const buildUrl = (
  from: string,
  to: string,
  engagement: boolean,
  brandPopulate: boolean,
): string => {
  const query = queryString({
    filter: {
      //* 발인일 기준. isDeleted는 절대 필터하지 않는다 (과거 기간이 통째로 비어 보인다)
      'deceasedInfo.coffinOut.date': {
        $gte: kstDayStartISO(from),
        $lte: kstDayEndISO(to),
      },
    },
    populate: populateOf(engagement, brandPopulate),
    options: {
      limit: FEVENT_FETCH_LIMIT,
      sort: { 'deceasedInfo.coffinOut.date': 1 },
    },
  });
  //* queryString은 encode:false라 공백이 든 select를 직접 인코딩해야 URL이 깨지지 않는다
  return `admin/fevent?${query}&select=${encodeURIComponent(FEVENT_SELECT)}`;
};

interface BugoStatPayload {
  fevents: BugoStatFevent[];
  truncated: boolean;
  /** 참여 수치를 실제로 받아왔는지 (켰는데 서버가 거부하면 false) */
  engagementLoaded: boolean;
}

export interface AdminBugoStatState {
  status: 'idle' | 'loading' | 'success' | 'error';
  fevents: BugoStatFevent[];
  /** 발송·미발송 수치를 쓸 수 없는 상태 (안 켰거나, 켰는데 서버가 거부) */
  bugoDataUnavailable: boolean;
  /** 조의글 개수를 쓸 수 없는 상태 */
  acqMsgDataUnavailable: boolean;
  /** 사용자가 참여 수치를 켜고 조회했는지 — 안내 문구가 갈린다 */
  engagementIncluded: boolean;
  /** FEVENT_FETCH_LIMIT에 걸려 잘렸는지 */
  truncated: boolean;
  errorMessage: string | null;
}

const EMPTY_FEVENTS: BugoStatFevent[] = [];

/**
 * 서버 message를 살린 오류 — 화면 문구는 여기서 확정하고 `response`는 그대로 얹어
 * 캐시 훅의 404 판별이 계속 동작하게 한다.
 */
const toStatError = (err: unknown): Error => {
  const res = (err as { response?: { status?: number; data?: { message?: string } } })
    ?.response;
  //* 백엔드 전역 필터가 error 코드를 덮어써서 400에서만 서버 message가 의미 있다
  const message =
    res?.status === 400 && res.data?.message
      ? res.data.message
      : res?.status === 401 || res?.status === 403
      ? '조회 권한이 없습니다.'
      : '통계를 불러오지 못했습니다.';
  return Object.assign(new Error(message), { response: res });
};

//* StrictPopulate는 500으로 떨어진다. 그 외 오류(권한·네트워크)는 그대로 올린다
const isPopulateRejection = (err: unknown) => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 500 || status === 400;
};

/**
 * 발인일 기준 상가 원자료 조회.
 * 전용 집계 엔드포인트가 없어 /admin/fevent 제네릭 조회로 받아 프론트에서 집계한다.
 *
 * ⚠️ 한 번에 최대 2만 건이다. 재조회를 아끼는 게 이 훅의 절반이라 캐시를 태운다 —
 * 탭 왕복·기간 되돌리기·재진입에는 요청이 나가지 않고, 연타해도 조회는 한 번만 돈다.
 *
 * `includeEngagement`를 켜면 부고·조의글 virtual populate가 붙는다(무겁다).
 * 구서버가 populate를 거부하면(StrictPopulate 500) **한 번만** 참여 수치 없이 재시도하고
 * 그 수치만 '집계 불가'로 표시한다. 예전엔 full→bugoOnly→none 3단이라 실패 한 번에
 * 2만 건 조회가 3회 나갔다.
 */
export const useAdminBugoStat = (
  from: string,
  to: string,
  includeEngagement = false,
): AdminBugoStatState & {
  resolveBrandName: BrandNameResolver;
  refetch: () => void;
} => {
  //* 이름 맵이 정해져야 brand populate를 붙일지 말지가 정해진다 (세션 캐시라 보통 즉시)
  const { status: brandStatus, resolveBrandName, brandPopulateFallback } =
    useAnalyticsBrandNames();

  const enabled = !!from && !!to && from <= to && brandStatus !== 'loading';
  const key = `adminBugoStat|${from}|${to}|${includeEngagement ? 'eng' : '-'}|${
    brandPopulateFallback ? 'pop' : '-'
  }`;

  const { data, loading, error, refetch } = useCachedResource<BugoStatPayload>(
    key,
    async () => {
      const load = async (engagement: boolean) => {
        const { data: rows } = await api_.get<BugoStatFevent[]>(
          buildUrl(from, to, engagement, brandPopulateFallback),
        );
        return Array.isArray(rows) ? rows : [];
      };

      try {
        let engagementLoaded = includeEngagement;
        let fevents: BugoStatFevent[];
        try {
          fevents = await load(includeEngagement);
        } catch (err) {
          if (!includeEngagement || !isPopulateRejection(err)) throw err;
          //* populate만 거부된 경우 — 참여 수치를 포기하고 상가 집계는 살린다
          fevents = await load(false);
          engagementLoaded = false;
        }
        return {
          fevents,
          truncated: fevents.length >= FEVENT_FETCH_LIMIT,
          engagementLoaded,
        };
      } catch (err) {
        throw toStatError(err);
      }
    },
    { enabled, errorMessage: '통계를 불러오지 못했습니다.' },
  );

  const engagementLoaded = data?.engagementLoaded ?? false;

  return {
    status: error ? 'error' : loading ? 'loading' : data ? 'success' : 'idle',
    fevents: data?.fevents ?? EMPTY_FEVENTS,
    bugoDataUnavailable: !engagementLoaded,
    acqMsgDataUnavailable: !engagementLoaded,
    engagementIncluded: includeEngagement,
    truncated: data?.truncated ?? false,
    errorMessage: error,
    resolveBrandName,
    refetch,
  };
};
